import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

const subscribers = new Set()

const emit = () => {
  for (const callback of subscribers) {
    callback()
  }
}

const subscribe = (callback) => {
  subscribers.add(callback)
  window.addEventListener('storage', callback)

  return () => {
    subscribers.delete(callback)
    window.removeEventListener('storage', callback)
  }
}

const readRawItem = (key) => {
  try {
    return window.localStorage.getItem(key)
  } catch (error) {
    console.warn(`Unable to read "${key}" from localStorage`, error)
    return null
  }
}

const writeItem = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.warn(`Unable to write "${key}" to localStorage`, error)
    return
  }

  emit()
}

const removeItem = (key) => {
  try {
    window.localStorage.removeItem(key)
  } catch (error) {
    console.warn(`Unable to remove "${key}" from localStorage`, error)
    return
  }

  emit()
}

export const isReadableRawItem = (raw) => {
  if (raw === null || raw === undefined) {
    return true
  }

  try {
    JSON.parse(raw)
    return true
  } catch (error) {
    return false
  }
}

export const parseRawItem = (raw, fallback) => {
  if (raw === null || raw === undefined) {
    return fallback
  }

  try {
    const parsed = JSON.parse(raw)
    return parsed === null || parsed === undefined ? fallback : parsed
  } catch (error) {
    console.warn('Discarding unreadable localStorage value', error)
    return fallback
  }
}

export const useLocalStorage = (key, initialValue) => {
  const fallbackRef = useRef(initialValue)
  fallbackRef.current = initialValue

  const raw = useSyncExternalStore(
    subscribe,
    () => readRawItem(key),
    () => null
  )

  useEffect(() => {
    const current = readRawItem(key)

    if (current !== null && isReadableRawItem(current)) {
      return
    }

    if (fallbackRef.current === undefined) {
      return
    }

    writeItem(key, fallbackRef.current)
  }, [key, raw])

  const setValue = useCallback(
    (value) => {
      const current = parseRawItem(readRawItem(key), fallbackRef.current)
      const nextValue = typeof value === 'function' ? value(current) : value

      if (nextValue === null || nextValue === undefined) {
        removeItem(key)
        return
      }

      writeItem(key, nextValue)
    },
    [key]
  )

  return [parseRawItem(raw, initialValue), setValue]
}
